import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Inbox, X, AtSign, MessageCircle, Bell } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore } from '@/store'
import { users } from '@/mocks'
import { Avatar } from '../../common'

const Panel = styled(View, {
  position: 'absolute',
  top: 40,
  right: 8,
  width: 420,
  maxHeight: 520,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  flexDirection: 'column',
  overflow: 'hidden',
  zIndex: 4000,
})

const PanelHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const CloseButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const TabBar = styled(View, {
  flexDirection: 'row',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const Tab = styled(View, {
  flex: 1,
  height: 40,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const NotifItem = styled(View, {
  flexDirection: 'row',
  gap: 10,
  paddingHorizontal: 16,
  paddingVertical: 10,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const UnreadDot = styled(View, {
  width: 8,
  height: 8,
  borderRadius: '$borderRadiusSm',
  backgroundColor: '$accent',
  position: 'absolute',
  top: 12,
  left: 8,
})

const EmptyState = styled(View, {
  alignItems: 'center',
  justifyContent: 'center',
  padding: 48,
  gap: 12,
})

type InboxTab = 'all' | 'mentions' | 'unreads'

// Mock notification data
const mockNotifications = [
  {
    id: 'n1',
    type: 'mention' as const,
    userId: 'u2',
    channel: '#general',
    content: 'Hey @Nova, can you review the latest commit?',
    timestamp: Date.now() - 300000,
    read: false,
  },
  {
    id: 'n2',
    type: 'reply' as const,
    userId: 'u3',
    channel: '#dev-ops',
    content: 'That approach looks good to me. Let me test it.',
    timestamp: Date.now() - 1200000,
    read: false,
  },
  {
    id: 'n3',
    type: 'mention' as const,
    userId: 'u5',
    channel: '#random',
    content: '@Nova @everyone check out this article about E2E encryption',
    timestamp: Date.now() - 3600000,
    read: true,
  },
  {
    id: 'n4',
    type: 'reply' as const,
    userId: 'u4',
    channel: '#general',
    content: 'The spatial audio feature is amazing. Great work on the implementation.',
    timestamp: Date.now() - 7200000,
    read: true,
  },
]

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp
  const minutes = Math.floor(diff / 60000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export function InboxView() {
  const theme = useTheme()
  const { toggleInbox } = useAppStore()
  const [activeTab, setActiveTab] = useState<InboxTab>('all')

  const filtered = activeTab === 'mentions'
    ? mockNotifications.filter(n => n.type === 'mention')
    : activeTab === 'unreads'
    ? mockNotifications.filter(n => !n.read)
    : mockNotifications

  return (
    <Panel
      // @ts-expect-error -- web box-shadow
      style={{ boxShadow: theme.shadowCard?.val || 'none' }}
    >
      <PanelHeader>
        <View flexDirection="row" alignItems="center" gap={8}>
          <Inbox size={16} color="$accent" />
          <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$surfaceForeground">
            Inbox
          </Text>
          {mockNotifications.filter(n => !n.read).length > 0 && (
            <View
              backgroundColor="$accent"
              borderRadius="$borderRadiusXl"
              paddingHorizontal={6}
              paddingVertical={1}
            >
              <Text fontFamily="$fontBody" fontSize={11} fontWeight="700" color="$surface">
                {mockNotifications.filter(n => !n.read).length}
              </Text>
            </View>
          )}
        </View>
        <CloseButton onPress={toggleInbox}>
          <X size={16} color="$sidebarMuted" />
        </CloseButton>
      </PanelHeader>

      <TabBar>
        {([
          { id: 'all' as const, label: 'All', icon: Bell },
          { id: 'mentions' as const, label: 'Mentions', icon: AtSign },
          { id: 'unreads' as const, label: 'Unreads', icon: MessageCircle },
        ]).map((tab) => (
          <Tab
            key={tab.id}
            onPress={() => setActiveTab(tab.id)}
            borderBottomWidth={activeTab === tab.id ? 2 : 0}
            borderBottomColor="$accent"
          >
            <tab.icon size={14} color={activeTab === tab.id ? '$accent' : '$sidebarMuted'} />
            <Text
              fontFamily="$fontBody"
              fontSize={13}
              fontWeight={activeTab === tab.id ? '600' : '400'}
              color={activeTab === tab.id ? '$surfaceForeground' : '$sidebarMuted'}
            >
              {tab.label}
            </Text>
          </Tab>
        ))}
      </TabBar>

      <ScrollView flex={1} maxHeight={380} showsVerticalScrollIndicator={false}>
        {filtered.length === 0 ? (
          <EmptyState>
            <Inbox size={32} color="$color8" />
            <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
              You're all caught up!
            </Text>
          </EmptyState>
        ) : (
          filtered.map((notif) => {
            const user = users[notif.userId]
            if (!user) return null
            return (
              <NotifItem key={notif.id} position="relative">
                {!notif.read && <UnreadDot />}
                <View paddingLeft={notif.read ? 0 : 12}>
                  <Avatar name={user.displayName} color={user.roleColor || '#8B95A8'} size="sm" showStatus={false} />
                </View>
                <View flex={1} gap={2}>
                  <View flexDirection="row" alignItems="baseline" gap={6}>
                    <Text fontFamily="$fontBody" fontSize={13} fontWeight="600" color={user.roleColor || '$surfaceForeground'}>
                      {user.displayName}
                    </Text>
                    <Text fontFamily="$fontMono" fontSize={10} color="$color8">
                      {notif.channel}
                    </Text>
                    <View flex={1} />
                    <Text fontFamily="$fontMono" fontSize={10} color="$color8">
                      {formatRelativeTime(notif.timestamp)}
                    </Text>
                  </View>
                  <Text fontFamily="$fontMono" fontSize={13} color="$surfaceForeground" numberOfLines={2}>
                    {notif.content}
                  </Text>
                  <View flexDirection="row" alignItems="center" gap={4} marginTop={2}>
                    {notif.type === 'mention' && <AtSign size={10} color="$accent" />}
                    {notif.type === 'reply' && <MessageCircle size={10} color="$sidebarMuted" />}
                    <Text fontFamily="$fontBody" fontSize={11} color="$color8">
                      {notif.type === 'mention' ? 'Mentioned you' : 'Replied to you'}
                    </Text>
                  </View>
                </View>
              </NotifItem>
            )
          })
        )}
      </ScrollView>
    </Panel>
  )
}
